/**
 * The dashboard's sections, and what a viewer needs to see each one.
 *
 * One list feeds both the sidebar and the routing, so a section cannot appear
 * in the navigation and be unreachable, or be reachable and invisible.
 */
import type { IconName } from '../ui/Icon';

export interface Section {
  id: IconName & string;
  label: string;
  group: string;
  /** One line under the heading: what this section is for. */
  description: string;
  /** Absent for a section every signed-in person may see. */
  capability?: string;
}

export const SECTIONS: readonly Section[] = [
  { id: 'applications', label: 'Applications', group: 'Recruitment', description: 'Everyone who applied through the careers page, newest first.', capability: 'applications:read' },
  { id: 'submissions', label: 'Submissions', group: 'Recruitment', description: 'Messages from the contact and community forms.', capability: 'enquiries:read' },
  { id: 'conversations', label: 'Assistant', group: 'Website', description: 'What visitors asked the website assistant.', capability: 'conversations:read' },
  { id: 'journal', label: 'Journal', group: 'Website', description: 'Write, publish and take down articles.', capability: 'journal:read' },
  { id: 'jobs', label: 'Job openings', group: 'Website', description: 'Draft roles, then publish them to the careers page.', capability: 'jobs:read' },
  { id: 'reports', label: 'Reports', group: 'Website', description: 'What people flagged in the matching app, and what to do about it.', capability: 'reports:read' },
  { id: 'invitations', label: 'Invitations', group: 'Website', description: 'Tell the people who signed up before the matching app opened that it is open.', capability: 'invitations:send' },
  { id: 'documents', label: 'Private documents', group: 'Organisation', description: 'Files only signed-in staff can download.', capability: 'documents:read' },
  { id: 'staff', label: 'Staff', group: 'Organisation', description: 'Who can sign in here, and what they may do.', capability: 'members:read' },
  { id: 'audit', label: 'Audit log', group: 'Organisation', description: 'Every change to staff access, in order. It cannot be edited.', capability: 'audit:read' },
  // No capability: every role has to be able to change the passphrase it was
  // handed, and an editor or a recruiter holds no capability at all.
  { id: 'account', label: 'Your account', group: 'System', description: 'Change the password you sign in with.' },
  { id: 'status', label: 'Platform status', group: 'System', description: 'Whether the backend is up and ready to serve.' },
];

/** What this viewer may see. The API checks the same rules again on every call. */
export function visibleSections(capabilities: readonly string[]): Section[] {
  return SECTIONS.filter(
    (section) => !section.capability || capabilities.includes(section.capability),
  );
}

/** The sidebar's groups, in the order they first appear. */
export function grouped(sections: readonly Section[]): { group: string; items: Section[] }[] {
  const groups: { group: string; items: Section[] }[] = [];
  for (const section of sections) {
    const current = groups[groups.length - 1];
    if (current && current.group === section.group) current.items.push(section);
    else groups.push({ group: section.group, items: [section] });
  }
  return groups;
}
